import { useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

// Mirrors the server-side regex in backend's newsletterController.js so
// both layers reject identical garbage and the inline UX matches what
// the API would return.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status = "idle" | "submitting" | "success" | "error";

/**
 * VOY-480: newsletter form wires to POST /api/newsletter/subscribe.
 * Success state replaces the form in-place; error state keeps the form
 * but surfaces an inline message so the user can retry without losing
 * what they typed.
 *
 * We use raw `fetch` (not the shared `apiFetch` wrapper) because that
 * wrapper throws on every non-ok response and surfaces a Spanish toast
 * unconditionally. For an inline newsletter form we need to read the
 * status code so a 400 (bad email) renders as an inline validation
 * message rather than a generic toast + "network error" double-fire.
 */
export function FooterCTA() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setErrorMessage("Please enter a valid email address.");
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setErrorMessage(null);

    // Inline auth: send the user's session token if signed in, else the
    // anonymous VITE_API_KEY. Mirrors apiFetch's auth strategy without
    // its throw-on-non-ok / global-toast behaviour.
    const session = (await supabase.auth.getSession()).data.session;
    const bearer = session?.access_token ?? import.meta.env.VITE_API_KEY;

    let res: Response;
    try {
      res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ email: trimmed, source: "landing-footer-cta" }),
      });
    } catch {
      setErrorMessage("Network error. Please try again.");
      setStatus("error");
      return;
    }

    if (res.ok) {
      setStatus("success");
      setEmail("");
      return;
    }

    // 400 = validation failure from the server. Everything else is a
    // generic retry prompt. Prefer the server's `error` field when
    // present so the user sees the specific reason (e.g. source length).
    const fallback =
      res.status === 400
        ? "Please enter a valid email address."
        : "Something went wrong. Please try again.";
    let serverMessage = fallback;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body?.error === "string" && body.error.length > 0) {
        serverMessage = body.error;
      }
    } catch {
      // Body wasn't JSON; keep fallback.
    }
    setErrorMessage(serverMessage);
    setStatus("error");
  };

  return (
    <section className="footer-cta">
      <div className="eyebrow" style={{ justifyContent: "center", display: "inline-flex" }}>
        <span className="bar" /> Stop paying the spread <span className="bar" />
      </div>
      <h2>
        The last travel app you'll <span className="italic">need.</span>
      </h2>
      <p style={{ color: "var(--v6-fg-body)", fontSize: 17, lineHeight: 1.6, maxWidth: 520, margin: "16px auto 0" }}>
        From $2.49/mo (Basic). Annual billing. Cancel anytime.
      </p>

      {status === "success" ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            maxWidth: 480,
            margin: "32px auto 0",
            padding: "14px 24px",
            borderRadius: 9999,
            border: "1px solid var(--v6-hair-strong)",
            background: "var(--v6-surface-well)",
            color: "var(--v6-fg-strong)",
            fontFamily: "var(--v6-font-sans)",
            fontSize: 14,
            textAlign: "center",
          }}
        >
          Got it. Check your inbox.
        </div>
      ) : (
        <>
          <form className="newsletter" onSubmit={handleSubmit}>
            <input
              type="email"
              placeholder="you@traveler.com"
              aria-label="Email"
              autoComplete="email"
              required
              disabled={status === "submitting"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? "Sending…" : "Start saving →"}
            </button>
          </form>
          {status === "error" && errorMessage && (
            <p
              role="alert"
              style={{
                maxWidth: 480,
                margin: "12px auto 0",
                color: "var(--v6-fg-body)",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {errorMessage}
            </p>
          )}
        </>
      )}
    </section>
  );
}
